import { useForm } from "react-hook-form";
import Modal from "../../../layouts/Modal";
import useEbay from "../../../data/useEbay";
import SelectInput from "../../shared/Inputs/SelectInput";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useNotification from "../../notifications/useNotifications";

export interface IBulkUpdatePolicies {
  locationKey?: string;
  paymentPolicyId?: string;
  fulfillmentPolicyId?: string;
  returnPolicyId?: string;
}

interface IBulkUpdatePoliciesModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (policiesToUpdate: IBulkUpdatePolicies) => void;
  isLoading: boolean;
  title?: string;
  description?: string;
  submitLabel?: string;
  showLocation?: boolean;
}

export default function BulkUpdatePoliciesModal({
  open,
  onClose,
  onSubmit,
  isLoading,
  title = "Bulk Update eBay Listing Policies",
  description = "Select the policies you want to update for the selected parts. Leave fields empty to keep existing values.",
  submitLabel = "Update Policies",
  showLocation = true,
}: IBulkUpdatePoliciesModalProps) {
  const { addNotification } = useNotification();
  const { policies, locations } = useEbay();

  const { register, setValue, watch, handleSubmit, reset } = useForm<IBulkUpdatePolicies>({
    defaultValues: {
      locationKey: undefined,
      paymentPolicyId: undefined,
      fulfillmentPolicyId: undefined,
      returnPolicyId: undefined,
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = (data: IBulkUpdatePolicies) => {
    const policiesToUpdate: IBulkUpdatePolicies = {};
    if (data.locationKey) policiesToUpdate.locationKey = data.locationKey;
    if (data.paymentPolicyId) policiesToUpdate.paymentPolicyId = data.paymentPolicyId;
    if (data.fulfillmentPolicyId) policiesToUpdate.fulfillmentPolicyId = data.fulfillmentPolicyId;
    if (data.returnPolicyId) policiesToUpdate.returnPolicyId = data.returnPolicyId;

    if (Object.keys(policiesToUpdate).length === 0) {
      addNotification({ primaryText: "Please select at least one policy to update", variant: "warn" });
      return;
    }

    onSubmit(policiesToUpdate);
    reset();
  };

  return (
    <Modal open={open} setOpen={handleClose} width="max-w-5xl">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className="space-y-4">
          <h2 className="text-lg font-medium leading-6 text-gray-900">{title}</h2>
          <p className="text-sm text-gray-600">
            {description}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {showLocation && (
              <SelectInput label={"Location of Part"} register={register} registerName={"locationKey"} options={locations.data?.data.locations || []} setValue={setValue} watch={watch} />
            )}
            <SelectInput label={"Shipping Policy"} register={register} registerName={"fulfillmentPolicyId"} options={policies.data?.data.fulfilmentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Payment Policy"} register={register} registerName={"paymentPolicyId"} options={policies.data?.data.paymentPolicies || []} setValue={setValue} watch={watch} />
            <SelectInput label={"Returns Policy"} register={register} registerName={"returnPolicyId"} options={policies.data?.data.returnPolicies || []} setValue={setValue} watch={watch} />
          </div>
          <div className="flex justify-end gap-4 pt-4 border-t">
            <ButtonInput label={"Cancel"} isSubmit={false} onClick={handleClose} classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"} />
            <ButtonInput label={submitLabel} isSubmit={true} onClick={undefined} classes={undefined} isLoading={isLoading} />
          </div>
        </div>
      </form>
    </Modal>
  );
}
